import React, { useState, useEffect } from 'react';
import { Plus, Shield, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge, ConfirmDialog } from '../components/ui';
import { usuariosService, departamentosService } from '../services/api';
import { roleColors, getInitials } from '../utils/formatters';
import { ROLES } from '../utils/constants';
import toast from 'react-hot-toast';

const UsuariosPage = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    nombre: '',
    telefono: '',
    rol: 'PROPIETARIO',
    departamento_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, deptosRes] = await Promise.all([
        usuariosService.getAll(),
        departamentosService.getAll()
      ]);
      setUsuarios(usersRes.data.data || []);
      setDepartamentos(deptosRes.data.data || []);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form };
      if (selected && !data.password) delete data.password;

      if (selected) {
        await usuariosService.update(selected.id, data);
        toast.success('Usuario actualizado');
      } else {
        await usuariosService.create(data);
        toast.success('Usuario creado');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEdit = (user) => {
    setSelected(user);
    setForm({
      username: user.username,
      email: user.email,
      password: '',
      nombre: user.nombre,
      telefono: user.telefono || '',
      rol: user.rol,
      departamento_id: user.departamento_id || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await usuariosService.delete(selected.id);
      toast.success('Usuario desactivado');
      setConfirmOpen(false);
      setSelected(null);
      loadData();
    } catch (error) {
      toast.error('Error al desactivar');
    }
  };

  const resetForm = () => {
    setSelected(null);
    setForm({
      username: '',
      email: '',
      password: '',
      nombre: '',
      telefono: '',
      rol: 'PROPIETARIO',
      departamento_id: ''
    });
    setShowPassword(false);
  };

  const getRolVariant = (rol) => {
    const variants = {
      ADMIN: 'purple',
      PRESIDENTE: 'warning',
      TESORERO: 'success',
      SECRETARIO: 'info',
      PROPIETARIO: 'default',
      PORTERO: 'info'
    };
    return variants[rol] || 'default';
  };

  const columns = [
    {
      header: 'Usuario',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${roleColors[row.rol] || 'from-slate-500 to-slate-600'} flex items-center justify-center text-sm font-bold text-white`}>
            {row.avatar || getInitials(row.nombre)}
          </div>
          <div>
            <p className="font-medium text-white">{row.nombre}</p>
            <p className="text-xs text-slate-500">@{row.username}</p>
          </div>
        </div>
      )
    },
    { header: 'Email', render: (row) => <span className="text-slate-400">{row.email}</span> },
    { 
      header: 'Rol', 
      render: (row) => (
        <Badge variant={getRolVariant(row.rol)}>
          {ROLES.find(r => r.value === row.rol)?.label || row.rol}
        </Badge>
      )
    },
    { header: 'Dpto', render: (row) => row.departamento_numero || '-' },
    { 
      header: 'Estado', 
      render: (row) => (
        <Badge variant={row.activo ? 'success' : 'danger'}>
          {row.activo ? 'Activo' : 'Inactivo'}
        </Badge>
      )
    },
    {
      header: 'Acciones',
      render: (row) => (
        <div className="flex gap-2">
          <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white">
            <Edit size={16} />
          </button>
          <button onClick={() => { setSelected(row); setConfirmOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400">
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  const usersByRole = ROLES.map(r => ({
    ...r,
    count: usuarios.filter(u => u.rol === r.value && u.activo).length
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Usuarios</h1>
          <p className="text-slate-400">Gestión de usuarios del sistema</p>
        </div>
        <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>
          Nuevo Usuario
        </Button>
      </div>

      {/* Stats por rol */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {usersByRole.map((rol) => (
          <Card key={rol.value} className="p-4">
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-slate-500" />
              <div>
                <p className="text-xs text-slate-400">{rol.label}</p>
                <p className="text-lg font-bold text-white">{rol.count}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <Table columns={columns} data={usuarios} loading={loading} emptyMessage="No hay usuarios" />
      </Card>

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Usuario' : 'Nuevo Usuario'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nombre completo"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              required
            />
            <Input
              label="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              disabled={!!selected}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <Input
              label="Teléfono"
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Rol"
              value={form.rol}
              onChange={(e) => setForm({ ...form, rol: e.target.value })}
              options={ROLES}
              required
            />
            <Select
              label="Departamento"
              value={form.departamento_id}
              onChange={(e) => setForm({ ...form, departamento_id: e.target.value })}
              options={departamentos.map(d => ({ value: d.id, label: `${d.numero} - ${d.propietario}` }))}
              placeholder="Sin asignar"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {selected ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!selected}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-xl py-2.5 px-4 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                placeholder={selected ? '••••••••' : 'Mínimo 6 caracteres'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">{selected ? 'Actualizar' : 'Crear'}</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Desactivar Usuario"
        message={`¿Estás seguro de desactivar a "${selected?.nombre}"? Podrás reactivarlo después.`}
        confirmText="Desactivar"
      />
    </div>
  );
};

export default UsuariosPage;
